import { P2Accordion, P2TextDescription, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script';
import { numberPattern } from '../../../utils/shared-pattern';
import { Occupier } from '../../../models/polymer/Occupier.model';
import { Owner } from '../../../models/polymer/Owner.model';
import { HoldingOwner } from '../../../models/polymer/HoldingOwner.model';
import { HoldingFormData } from '../../../models/polymer/HoldingFormData.model';
import { HoldingOccupier } from '../../../models/polymer/HoldingOccupier.model';
import { Separator } from '../../../components/separator';
import SingpassMyInfo from '../../../components/singpass/singpass-myinfo';
import useRetrieveMyInfoStageB from '../../../hooks/use-retrieve-myinfo-stage-b';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useCallback, useMemo } from 'react';
import { first } from 'lodash';
import { MyInfo } from '../../../models/polymer/MyInfo.model';
import { retrieveMyInfoByNRICDeclaration } from '../../../redux/slices/authentication-slice';
import { LottieLoader } from '../../../components/loader';
import { PolymerCrypto } from '../../../utils/encryptor';
import useCheckLoginIsAdditionalRole from '../../../hooks/use-check-login-role';
import Dropdown from '../../../components/dropdown/dropdown';
import { maritalStatusDropdownItems } from '../../../utils/shared-dropdown';

interface ReviewContactDetailsProps{
  proposed_owners?: Owner[],
  proposed_occupiers?: Occupier[],
  holdingSetFormValue?:Function,
  holdingFormValues?:HoldingFormData,
  holdingFormErrors?:any,
  isDisabled?: boolean,
  allowMyInfo?: boolean,
  onBindMyInfo?: (nric: string, myInfoData:MyInfo) => void
  onUnBindMyInfo?: (nric: string) => void
}

function ReviewContactDetails({proposed_owners=[], proposed_occupiers=[],isDisabled=false, allowMyInfo=false ,...props}:ReviewContactDetailsProps) {
  const AppDispatch = useAppDispatch();
  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const applicationStartFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const mainApplicantUinfin = applicationStartFormData.selected_hdb_property?.main_applicant_uinfin;
  const { result:isAdditionalRole } = useCheckLoginIsAdditionalRole()

  const firstProposedOwners = useMemo(()=>{
    const firstElement = first(proposed_owners);
    return firstElement;
  },[proposed_owners])
  
  const isLoginOutgoing = useMemo(() => {
      return applicationStep1FormData.household.incoming_owners.every(item => item.myinfo.uinfin !== loginUser.uinfin);
  }, [loginUser.uinfin, applicationStep1FormData.household])

  const bindProcess = useCallback((res:any)=>{
    const errorMessage = "We are unable to retrieve your information from Myinfo. Please enter your personal details manually.";
    if(isNaN(res.payload)){
      const myInfoData:MyInfo = new MyInfo({...res.payload,  uinfin: PolymerCrypto.decryption(res.payload.uinfin), mobile_no: PolymerCrypto.decryption(res.payload.mobile_no), email: PolymerCrypto.decryption(res.payload.email)});
      if(myInfoData.dob && myInfoData.dob.split("-").length < 3) {
        setRetrieve(false)
        alert(errorMessage)
        return;
      }
      if(props.onBindMyInfo && firstProposedOwners?.myinfo.uinfin){
        props.onBindMyInfo(firstProposedOwners?.myinfo.uinfin, myInfoData);
      }
    } else {
      setRetrieve(false)
      alert(errorMessage)
    }
  },[firstProposedOwners?.myinfo.uinfin, props])



  //NOTE : alwaysShow params set to (false || isAdditionalRole), to allow additionalRole to retrieveMyinfo
  const { isRetrieve, isMyInfoLoading, toShowMyInfo, toggleRetrieveMyInfo, myInfoLoadingValue, setRetrieve  } = useRetrieveMyInfoStageB(()=>{
    AppDispatch(retrieveMyInfoByNRICDeclaration({ uinfin: firstProposedOwners?.myinfo.uinfin, singpass_code: singpassToken, allow_my_info: true })).then(bindProcess).finally(()=>{
      myInfoLoadingValue(false);
    });
  }, ()=>{
    AppDispatch(retrieveMyInfoByNRICDeclaration({ uinfin: firstProposedOwners?.myinfo.uinfin, singpass_code: singpassToken, allow_my_info: false })).finally(()=>{
      if(props.onUnBindMyInfo && firstProposedOwners?.myinfo.uinfin){
        props.onUnBindMyInfo(firstProposedOwners?.myinfo.uinfin);
      }
    }).finally(()=>{
      myInfoLoadingValue(false);
    });
  }, firstProposedOwners?.myinfo.uinfin ?? "", isAdditionalRole , true);
  
  

  const holdingIncomingOwnerHandler = useCallback((id:string, values:string | boolean,type:string) =>{
    let tempValue = props.holdingFormValues?.holding_owners;
    let new_array: HoldingOwner[] = [];
  
    tempValue?.forEach(e => {
      if(e.myinfo?.uinfin === id){
        new_array.push({ ...e, myinfo: {...e.myinfo, [type.toString()]: values.toString() }}) 
      }
      else{
        new_array.push({...e});
      }
    });

    if(props.holdingSetFormValue){
      props.holdingSetFormValue({...props.holdingFormValues, holding_owners: new_array})
    }
  },[props])


  const holdingIncomingOccupierHandler = useCallback((id:string, values:string | boolean,type:string) =>{
    let tempValue = props.holdingFormValues?.holding_occupiers;
    let new_array: HoldingOccupier[] = [];
    
    tempValue?.forEach(e => {
      if(e.myinfo?.uinfin === id){
        new_array.push({ ...e, myinfo: {...e.myinfo, [type.toString()]: values.toString() }}) 
      }
      else{
        new_array.push({...e});
      }
    });
    if(props.holdingSetFormValue){
      props.holdingSetFormValue({...props.holdingFormValues, holding_occupiers:new_array})
    }
  },[props])

  const handleValueChangeOwner = useCallback((id: string, key: string) => (value: string | boolean) => {
    holdingIncomingOwnerHandler(id, value, key);
  },[holdingIncomingOwnerHandler])


  /**
   * Note: currently the occupier will always return empty array, so will not be used. To amend if it is use in the future.
   */
  const handleValueChangeOccupier = useCallback((id: string, key: string) => (value: string | boolean) => {
    holdingIncomingOccupierHandler(id, value, key);
  },[holdingIncomingOccupierHandler])
  

  const handleRetrieveMyInfo = useCallback(()=>{
    toggleRetrieveMyInfo()
  },[toggleRetrieveMyInfo])

  return (
    <div className='px-4 pb-4'>
      {proposed_owners.length !== 0  && <div style={{marginTop: 24}}>
        {
          proposed_owners.map( (owner, _index:number) =>{

            let temp: any = props.holdingFormErrors;
            let error = temp?.holding_owners ? temp?.holding_owners[_index]! : {};
            let email = "********";
            let mobile = "********";

            if(mainApplicantUinfin === loginUser?.uinfin || !isDisabled){//only show when main applicant is the same with the login user, or isDisabled is false/undefined(stage A5)
              email = owner?.myinfo?.email;
              mobile = owner?.myinfo?.mobile_no;
            }
            return ( <div key={owner.myinfo?.uinfin} className='mt-2'>
                <P2Accordion
                  defaultOpen={true}
                  title={
                    <P2TextDescription size='18px' color="#333333" title={"Declared and authorised by:"}/>
                  }
                >
                  {isMyInfoLoading && <LottieLoader />}
                  {!isMyInfoLoading && <>
                    { !isDisabled && toShowMyInfo && <SingpassMyInfo hasRetrieve={isRetrieve} onClick={handleRetrieveMyInfo} />}
                    {<div className='mt-3 d-flex flex-column gap-3'>
                      <div className="polymer-summary-contact-details"><P2TextInput disabled={true} required value={mainApplicantUinfin !== undefined? owner.myinfo.name : loginUser.name} name='name' label='Name' placeholder='Please enter full name' type='text' /></div>
                        {isLoginOutgoing && <Dropdown disabled={isRetrieve || isDisabled} required selectedDropdown={owner?.myinfo?.marital_status} placeholder="Please indicate marital status" label="Marital status" items={maritalStatusDropdownItems} setSelectedDropdown={handleValueChangeOwner(owner.myinfo?.uinfin,"marital_status")}/>}
                        <P2TextInput disabled={isRetrieve || isDisabled} isError={!error?.myinfo?.mobile_no?.isValid} errorMsg={error?.myinfo?.mobile_no?.message} maxLength={8}  pattern={numberPattern} required value={mobile} name='contact_no' label='Contact number' placeholder='Please enter contact number.' type='text' onChange={handleValueChangeOwner(owner.myinfo?.uinfin, "mobile_no")}/>
                        <P2TextInput disabled={isRetrieve || isDisabled} isError={!error?.myinfo?.email?.isValid} errorMsg={error?.myinfo?.email?.message} maxLength={255}  required value={email} name='email' label='Email address' placeholder='Please enter email address' type='text' onChange={handleValueChangeOwner(owner.myinfo?.uinfin, "email")}/>
                      </div>}
                  </>}
                </P2Accordion>
              </div> 
            )
          })
        }
      </div>}

      {/*Occupiers will be empty array.*/}
      {proposed_occupiers.length !== 0 && 
        <>
          <Separator style={{margin:"32px 0 16px"}}/>
          <div className='mt-4'>
            <P2TextLabel bold="700" color={"#000"} label={"Declared and authorised by"}/>
            {
              proposed_occupiers.map( (occupier, _index:number) =>{
                let temp: any = props.holdingFormErrors;
                let error = temp?.holding_occupiers ? temp?.holding_occupiers[_index]! : {};

                return ( <div key={occupier.myinfo?.uinfin} className='mt-2'>
                    <P2Accordion
                      defaultOpen={true}
                      title={
                        <P2TextDescription size='18px' color="#333333" title={occupier.myinfo?.name}/>
                      }
                    >
                      <div className='mt-3 d-flex flex-column gap-3'>
                      <div className="polymer-summary-contact-details"><P2TextInput disabled={true} required value={occupier.myinfo?.name} name='name' label='Name' placeholder='Please enter full name' type='text' /></div>
                        <P2TextInput disabled={isDisabled} isError={!error?.myinfo?.mobile_no?.isValid} errorMsg={error?.myinfo?.mobile_no?.message} maxLength={8}  pattern={numberPattern} required value={occupier.myinfo?.mobile_no} name='contact_no' label='Contact number' placeholder='Please enter contact number.' type='text' onChange={handleValueChangeOccupier(occupier.myinfo?.uinfin, "mobile_no")}/>
                        <P2TextInput disabled={isDisabled} isError={!error?.myinfo?.email?.isValid} errorMsg={error?.myinfo?.email?.message} maxLength={255}  required value={occupier.myinfo?.email} name='email' label='Email address' placeholder='Please enter contact number.' type='text' onChange={handleValueChangeOccupier(occupier.myinfo?.uinfin, "email")}/>
                      </div>
                    </P2Accordion>
                  </div> 
                )
              })
            }
          </div>
        </>
      }

    </div>
  )
}

export default ReviewContactDetails
